import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserCircle, LogOut, Loader2, CloudUpload, Trash2 } from "lucide-react";
import { useUser } from "@/components/contexts/UserContext";
import { useStoredList } from "@/lib/storage";

/**
 * Optional account.
 *
 * The page leads with what the visitor already has locally, because signing up is
 * never required to use the library - the only thing an account buys is the same
 * library on another device. Copy here says that plainly rather than implying a
 * signup wall.
 */
export const AccountPage = () => {
  const { user, isSignedIn, loading, syncing, login, register, logout, deleteAccount, syncLibrary, token } =
    useUser();
  const navigate = useNavigate();

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [history] = useStoredList("history");
  const [saved] = useStoredList("saved");
  const [follows] = useStoredList("follows");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "register" && password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      if (mode === "register") {
        await register(email, password);
        toast.success("Account created — your library has been synced");
      } else {
        await login(email, password);
        toast.success("Signed in — your library has been synced");
      }
      setPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign in");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete your account and everything stored on the server? Your on-device library stays.")) {
      return;
    }
    try {
      await deleteAccount();
      toast.success("Account deleted");
      navigate("/");
    } catch {
      toast.error("Could not delete the account");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (isSignedIn) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4" data-testid="account-page">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="flex items-center gap-3 mb-6">
            <UserCircle className="w-12 h-12 text-orange-500" />
            <div className="min-w-0">
              <h1 className="font-heading text-xl font-black truncate">{user.email}</h1>
              <p className="text-xs text-white/40">Library syncs across your devices</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: "Watched", value: history.length },
              { label: "Saved", value: saved.length },
              { label: "Following", value: follows.length },
            ].map((stat) => (
              <div key={stat.label} className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                <p className="font-heading text-xl font-bold">{stat.value}</p>
                <p className="text-[11px] text-white/40">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <button
              onClick={async () => {
                const result = await syncLibrary(token);
                toast[result ? "success" : "error"](result ? "Library synced" : "Sync failed");
              }}
              disabled={syncing}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
              Sync now
            </button>

            <button
              onClick={() => {
                logout();
                toast.success("Signed out — your library stays on this device");
              }}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>

            <button
              onClick={handleDelete}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm text-white/40 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" /> Delete account
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-white/30">
            <NavLink to="/privacy" className="hover:text-orange-400 transition-colors">
              Privacy Policy
            </NavLink>
            <NavLink to="/terms" className="hover:text-orange-400 transition-colors">
              Terms of Service
            </NavLink>
            <NavLink to="/delete-account" className="hover:text-orange-400 transition-colors">
              Delete account &amp; data
            </NavLink>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="account-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto"
      >
        <h1 className="font-heading text-2xl font-black mb-2">
          {mode === "register" ? "Create an account" : "Sign in"}
        </h1>
        <p className="text-sm text-white/50 mb-6">
          Optional. Your history, saves and follows already work on this device — an
          account just carries them to your other devices.
          {(history.length > 0 || saved.length > 0 || follows.length > 0) && (
            <>
              {" "}
              The {history.length + saved.length + follows.length} item
              {history.length + saved.length + follows.length === 1 ? "" : "s"} stored here
              will be merged in.
            </>
          )}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="account-email" className="block text-sm text-white/60 mb-2">
              Email
            </label>
            <input
              id="account-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="account-password" className="block text-sm text-white/60 mb-2">
              Password
            </label>
            <input
              id="account-password"
              type="password"
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === "register" ? 8 : undefined}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:outline-none"
            />
            {mode === "register" && (
              <p className="text-[11px] text-white/30 mt-1">At least 8 characters.</p>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-heading font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "register" ? "Create account" : "Sign in"}
          </button>
        </form>

        {/* Shown at the point of collection, not buried in a settings screen:
            both Play's policy and the GDPR expect the notice to be next to the
            control that submits the data. */}
        <p className="mt-4 text-[11px] leading-relaxed text-white/30 text-center">
          By continuing you agree to the{" "}
          <NavLink to="/terms" className="text-white/50 hover:text-orange-400 underline underline-offset-2">
            Terms of Service
          </NavLink>{" "}
          and{" "}
          <NavLink to="/privacy" className="text-white/50 hover:text-orange-400 underline underline-offset-2">
            Privacy Policy
          </NavLink>
          . You can delete your account at any time.
        </p>

        <button
          onClick={() => setMode(mode === "register" ? "login" : "register")}
          className="w-full mt-6 text-sm text-orange-400 hover:text-orange-300 transition-colors"
        >
          {mode === "register" ? "Already have an account? Sign in" : "No account? Create one"}
        </button>
      </motion.div>
    </div>
  );
};
